import { NoPermissionError } from '@/api/NoPermissionError';
import {
    listHeaderQueryOptions,
    listMemberAssignedQueryOptions,
    listMemberAvailableQueryOptions,
    useAssignListMemberMutation,
    useUnassignListMemberMutation
} from '@/api/listQueryOptions';
import {
    ListMemberType
} from '@/api/lists';
import { MemberType } from '@/api/members';
import LoadProgress from '@/components/LoadProgress';
import { NoPermissionErrorComponent } from '@/components/NoPermissionErrorComponent';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ListIcon from '@mui/icons-material/List';
import {
    Box,
    Checkbox,
    Chip,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Select,
    Stack,
    styled,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, ErrorComponent, Link } from '@tanstack/react-router';
import { useState } from 'react';

import { enqueueSnackbar } from 'notistack';

import { DuplicateRecordError } from '@/api/DuplicateRecordError';
import { isActive } from '@/api/dateUtils';
import { MemberJobType } from '@/api/mem/memberJob';
import { MemberSectionType } from '@/api/mem/memberSection';
import { OrganizationReferenceType } from '@/api/organizations';
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions';
import ScoutIdAutocomplete from '@/components/ScoutIdAutocomplete';
import useResponsive from '@/components/hooks/useResponsive';
import ListEditForm from '@/components/list/ListEditForm';
import ListMemberList from '@/components/list/ListMemberList';
import { getOrganization } from '@/store/auth/authSlice';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import { t } from 'i18next';
import React from 'react';
import { useSelector } from 'react-redux';

export const Route = createFileRoute(
    '/_auth/list_/$listHeaderUuid',
)({
    component: ListHeaderComponent,
    errorComponent: ({ error }) => {
        if (error instanceof NoPermissionError) {
            return (
                <NoPermissionErrorComponent error={error}></NoPermissionErrorComponent>
            )
        }
        return <ErrorComponent error={error}></ErrorComponent>
    },
})

function ListHeaderComponent() {
    const params = Route.useParams()
    const sessionOrganization = useSelector(getOrganization)

    const listInstanceQuery = useSuspenseQuery(
        listHeaderQueryOptions(params.listHeaderUuid),
    )
    const listHeader = listInstanceQuery.data

    const listMemberAssignedQuery = useSuspenseQuery(
        listMemberAssignedQueryOptions(params.listHeaderUuid),
    )
    const listMemberAssigned = listMemberAssignedQuery.data

    const listMemberAvailableQuery = useSuspenseQuery(
        listMemberAvailableQueryOptions(params.listHeaderUuid),
    )
    const listMemberAvailable = listMemberAvailableQuery.data

    const permissionsQuery = useSuspenseQuery(
        fetchDetailPermissionsQueryOptions('organization', sessionOrganization.uuid, [
            'ListMember',
        ]),
    )
    const permissions = permissionsQuery.data

    const createListMember =
        permissions &&
        permissions.filter(
            (p) =>
                p.detail === 'ListMember' && p.permissions.some((a) => a === 'create'),
        ).length > 0

    const assignMemberMutation = useAssignListMemberMutation(
        params.listHeaderUuid,
    )
    const unassignMemberMutation = useUnassignListMemberMutation()

    const [selectedAvailableMembers, setSelectedAvailableMembers] = useState<
        MemberType[]
    >([])
    const [selectedAssignedMembers, setSelectedAssignedMembers] = useState<
        ListMemberType[]
    >([])

    const handleSelectAvailableChange = (
        event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const { options } = event.target
        const selectedIds: string[] = []
        for (let i = 0, l = options.length; i < l; i += 1) {
            if (options[i].selected) {
                selectedIds.push(options[i].value)
            }
        }
        const selectedAvailableMembers = listMemberAvailable.filter((member) =>
            selectedIds.includes(member.uuid),
        )
        setSelectedAvailableMembers(selectedAvailableMembers)
    }

    const handleSelectAssignedChange = (
        event: React.ChangeEvent<HTMLSelectElement>,
    ) => {
        const { options } = event.target
        const selectedIds: string[] = []
        for (let i = 0, l = options.length; i < l; i += 1) {
            if (options[i].selected) {
                selectedIds.push(options[i].value)
            }
        }
        const selectedAssignedMembers = listMemberAssigned.filter((listMember) =>
            selectedIds.includes(listMember.uuid || ''),
        )
        setSelectedAssignedMembers(selectedAssignedMembers)
    }

    const assignMember = (mem: MemberType) => {
        // check if member already exists
        const existing = listMemberAssigned.filter(
            (listMember) => listMember.member.uuid == mem.uuid,
        )
        if (existing.length > 0) {
            enqueueSnackbar(t('error.record.exists'))
        } else {
            const promise = assignMemberMutation.mutateAsync(mem.uuid)
            promise
                .then((value) => {
                    // replace placeholder with real value
                    const listMembers = listMemberAssigned.filter(
                        (listMember) => listMember.member.uuid == value.member.uuid,
                    )
                    listMembers.map(
                        (listMember) =>
                        (listMemberAssigned[listMemberAssigned.indexOf(listMember)] =
                            value),
                    )
                    enqueueSnackbar(t('action.added', {
                        entity: value.member.lastname +
                            ' ' +
                            value.member.firstname
                    })
                    )
                })
                .catch((error) => {
                    // remove placeholder
                    const listMembers = listMemberAssigned.filter(
                        (listMember) =>
                            listMember.member.uuid == mem.uuid && !listMember.uuid,
                    )
                    listMembers.map((listMember) =>
                        listMemberAssigned.splice(
                            listMemberAssigned.indexOf(listMember),
                            1,
                        ),
                    )
                    if (error instanceof NoPermissionError) {
                        enqueueSnackbar(t('error.noPermission'))
                    } else if (error instanceof DuplicateRecordError) {
                        enqueueSnackbar(t('error.record.exists'))
                    } else {
                        enqueueSnackbar(error)
                    }
                })
            // const index = listMemberAvailable.indexOf(mem);
            // listMemberAvailable.splice(index, 1);
            listMemberAssigned.push({ uuid: '', member: mem } as ListMemberType)
            listMemberAvailable.push({ uuid: '' } as MemberType)
            listMemberAvailable.pop()
        }
    }

    const assignMembers = () => {
        selectedAvailableMembers.map((mem) => {
            assignMember(mem)
        })
    }

    const unassignMember = (mem: ListMemberType) => {
        const promise = unassignMemberMutation.mutateAsync(mem.uuid || '')
        promise.then((value) => {
            enqueueSnackbar(
                t('action.removed', {
                    entity: value.member.lastname + ' ' + value.member.firstname,
                }),
            )
        })
        const index = listMemberAssigned.indexOf(mem)
        listMemberAssigned.splice(index, 1)
        listMemberAvailable.push({ uuid: '' } as MemberType)
        listMemberAvailable.pop()
    }

    const unassignMembers = () => {
        console.log(selectedAssignedMembers)
        selectedAssignedMembers.map((mem) => {
            unassignMember(mem)
        })
    }

    const writeable = listHeader.permissions
        ? listHeader.permissions.some((perm) => perm === 'write')
        : false

    const assignable = listHeader.permissions
        ? listHeader.permissions.some((perm) => perm === 'assign')
        : false

    function isAssigned(
        member: MemberType,
        listMemberAssigned: ListMemberType[],
    ) {
        return listMemberAssigned
            .map((listMember) => listMember.member.uuid)
            .includes(member.uuid)
    }

    const handleToggle = (member: MemberType) => () => {
        const assigned = listMemberAssigned.filter(
            (lme) => member.uuid == lme.member.uuid,
        )
        if (assigned.length > 0) {
            assigned.forEach((lme) => unassignMember(lme))
        } else {
            assignMember(member)
        }
    }

    const Spacer = styled('div')(() => ({
        position: 'relative',
        width: '100%',
        display: 'inline'
    }));

    const { minSmall, minMedium } = useResponsive();

    const [searchValue, setSearchValue] = React.useState<string>('');
    const listMemberAvailableFiltered = listMemberAvailable.filter((member) => {
        if (searchValue && searchValue.length > 0) {
            const fullName = (member.firstname + ' ' + member.lastname).toLowerCase();
            return fullName.includes(searchValue.toLowerCase());
        }
        return true;
    })

    const renderJobs = (joblist: MemberJobType[]) => {
        if (minSmall) {
            return joblist.map((job: any) => (
                <Tooltip key={job.job.uuid} title={job.job.name}>
                    <Chip size='small' label={job.job.abbreviation} color='secondary'></Chip>
                </Tooltip>
            ))
        } else {
            const slicedJoblist = joblist.slice(0, 3);
            if (joblist.length > 3) {
                slicedJoblist.push({ job: { uuid: 'ellipsis', name: '...', abbreviation: '...' }, organization: { uuid: '', name: '', abbreviation: '' }, uuid: '' } as MemberJobType);
            }
            // limit to three jobs with ellipsis
            return slicedJoblist.map((job: any) => (
                <Tooltip key={job.job.uuid} title={job.job.name}>
                    <Chip size='small' label={job.job.abbreviation} color='secondary' variant={'...' === job.job.abbreviation ? 'outlined' : undefined}></Chip>
                </Tooltip>
            ))
        }
    }

    const renderSection = (sec: MemberSectionType) => {
        return (
            <Tooltip title={sec.section.name} key={sec.uuid}>
                <Chip color="primary" style={{ backgroundColor: isActive(sec.startDate, sec.endDate) ? sec.section.color : '#aaaaaa' }} label={sec.section.abbreviation} size="small" />
            </Tooltip>
        )
    }

    if (listInstanceQuery.isLoading || listInstanceQuery.isFetching) {
        return <LoadProgress />
    }

    if (listMemberAssignedQuery.isLoading || listMemberAssignedQuery.isFetching) {
        return <LoadProgress />
    }

    if (
        listMemberAvailableQuery.isLoading ||
        listMemberAvailableQuery.isFetching
    ) {
        return <LoadProgress />
    }

    if (permissionsQuery.isLoading || permissionsQuery.isFetching) {
        return <LoadProgress />
    }

    const pickListVisible = false

    return (
        <>
            <Box sx={{
                height: { xs: `calc(100% - 50px)` }
            }}>
                <Accordion disableGutters={true}>
                    <AccordionSummary>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}>

                            <ListIcon />
                            <span>
                                <b>
                                    {listHeader.name || ''}
                                </b>
                            </span>
                        </div>
                        <Box sx={{ flexGrow: 1 }} />
                        <Tooltip title={t('action.edit', { entity: t('entity.listHeader') })}>
                            <EditIcon />
                        </Tooltip>
                        <Box sx={{ width: '10px' }} />
                        <Link
                            to="/memberships"
                            search={{
                                filter: [
                                    { field: 'properties.listHeader', operator: 'isAnyOf', value: [listHeader.uuid] },
                                ],
                                sort: [],
                                visibility: [
                                    { field: 'property.joblist' },
                                    { field: 'property.listMember.text1' },
                                    { field: 'property.listMember.text2' },
                                ],
                            }}
                            onClick={() => {
                                localStorage.removeItem('scoreg.gridFilterModel.membership')
                            }}
                        >



                            <Tooltip title={t('tooltip.list.showInOverview')}>
                                <FormatListNumberedIcon />
                            </Tooltip>
                        </Link>
                    </AccordionSummary>
                    <AccordionDetails>
                        <ListEditForm listHeader={listHeader} />
                    </AccordionDetails>
                </Accordion>
                {(writeable || assignable) && createListMember && (
                    <Accordion disableGutters={true} >
                        <AccordionSummary expandIcon={<AddCircleOutlineIcon />} >
                            {minMedium &&
                                <Typography style={{ display: 'inline', whiteSpace: 'nowrap' }}>{t('action.addSingle', { entity: t('entity.member') })}</Typography>
                            }
                            <Spacer />
                            <Typography style={{ display: 'inline', whiteSpace: 'nowrap', paddingRight: '4px', fontSize: '0.85rem' }} variant="caption">{t('action.addSingle', { entity: t('entity.member') })}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <ScoutIdAutocomplete
                                buttonHandler={(member) => assignMember(member)}
                                buttonLabel={t('action.add', { entity: '' })}
                            ></ScoutIdAutocomplete>
                        </AccordionDetails>
                    </Accordion>
                )}
                {(writeable || assignable) && createListMember && listMemberAvailable.length > 0 && (
                    <Accordion disableGutters={true}>
                        <AccordionSummary expandIcon={<AddCircleOutlineIcon />}>
                            {minMedium &&
                                <Typography style={{ display: 'inline', whiteSpace: 'nowrap' }}>{t('action.addMultiple', { entity: t('entity.member') })}</Typography>
                            }
                            <Spacer />
                            <Typography style={{ display: 'inline', whiteSpace: 'nowrap', paddingRight: '4px', fontSize: '0.85rem' }} variant="caption">{t('action.addMultiple', { entity: t('entity.member') })}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        alignItems: "flex-end",
                                        pb: '10px'
                                    }}>

                                    <TextField
                                        value={searchValue}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                            setSearchValue(event.target.value);
                                        }}
                                        variant="standard"
                                        placeholder="Suchen..."
                                        slotProps={{
                                            input: {
                                                endAdornment: <InputAdornment position="end"><IconButton onClick={() => {
                                                    setSearchValue('')
                                                }}><ClearIcon /></IconButton></InputAdornment>,
                                            },
                                        }}
                                    />
                                </Stack>

                                <List style={{ height: '300px', overflow: 'scroll', width: '100%' }} sx={{ padding: '0px', margin: '0px' }} dense>
                                    {listMemberAvailableFiltered.map((member) => {
                                        const enhancedMember = member as MemberType & { mainOrganization: OrganizationReferenceType, jobsJson: string, sectionJson: string };
                                        return (
                                            <ListItem
                                                key={member.uuid}
                                                value={member.uuid}
                                                disablePadding
                                                dense
                                                disableGutters
                                                secondaryAction={
                                                    <>
                                                        {
                                                            enhancedMember.sectionJson && renderSection(JSON.parse(enhancedMember.sectionJson))
                                                        }
                                                        {
                                                            enhancedMember.jobsJson && renderJobs(JSON.parse(enhancedMember.jobsJson))
                                                        }
                                                        {
                                                            sessionOrganization && enhancedMember.mainOrganization &&
                                                            sessionOrganization.uuid !== enhancedMember.mainOrganization.uuid &&
                                                            <Tooltip title={enhancedMember.mainOrganization.name || ''}>
                                                                <Chip size='small' label={enhancedMember.mainOrganization.abbreviation} color='primary'></Chip>
                                                            </Tooltip>
                                                        }
                                                    </>
                                                }
                                            >
                                                <ListItemButton onClick={handleToggle(member)} dense sx={{ padding: '0px', margin: '0px' }}>
                                                    <ListItemIcon sx={{ padding: '0px ' }}>
                                                        <Checkbox sx={{ padding: '2px ' }}
                                                            checked={isAssigned(member, listMemberAssigned)}
                                                            disableRipple
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText sx={{ padding: '0px', margin: '0px' }}
                                                        primary={member.lastname + ' ' + member.firstname}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        )
                                    })}
                                </List>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                )}
                {pickListVisible && (writeable || assignable) && createListMember && listMemberAvailable.length > 0 && (
                    <Accordion disableGutters={true}>
                        <AccordionSummary expandIcon={<AddCircleOutlineIcon />} >
                            {minMedium &&
                                <Typography style={{ display: 'inline', whiteSpace: 'nowrap' }}>{t('action.add', { entity: t('entity.member') })}</Typography>
                            }
                            <Spacer />
                            <Typography style={{ display: 'inline', whiteSpace: 'nowrap', paddingRight: '4px', fontSize: '0.85rem' }} variant="caption">{t('action.add', { entity: t('entity.member') })}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container>
                                <Grid size={5}>
                                    <FormControl sx={{ m: 0, width: '100%' }}>
                                        <InputLabel shrink htmlFor="select-available">
                                            verfügbar
                                        </InputLabel>
                                        <Select<MemberType[]>
                                            multiple
                                            native
                                            label="verfügbar"
                                            style={{ height: '200px', overflow: 'hidden' }}
                                            // @ts-expect-error(bla)
                                            onChange={handleSelectAvailableChange}
                                            inputProps={{
                                                id: 'select-available',
                                                style: {
                                                    height: '200px',
                                                    paddingTop: '25px',
                                                    paddingLeft: '10px',
                                                    paddingRight: '10px',
                                                    overflow: 'scroll',
                                                },
                                            }}
                                        >
                                            {listMemberAvailable
                                                .filter(
                                                    (member) => !isAssigned(member, listMemberAssigned),
                                                )
                                                .map((member) => (
                                                    <option key={member.uuid} value={member.uuid}>
                                                        {member.lastname} {member.firstname}
                                                    </option>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={1}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            pt: 2
                                        }}>
                                        <Tooltip title="zuweisen">
                                            <IconButton onClick={assignMembers}>
                                                <ArrowCircleRightIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            pt: 2
                                        }}>
                                        <Tooltip title="entfernen">
                                            <IconButton onClick={unassignMembers}>
                                                <ArrowCircleLeftIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Grid>
                                <Grid size={5}>
                                    <FormControl sx={{ m: 0, width: '100%' }}>
                                        <InputLabel shrink htmlFor="select-assigned">
                                            zugewiesen
                                        </InputLabel>
                                        <Select<ListMemberType[]>
                                            multiple
                                            native
                                            label="zugewiesen"
                                            style={{ height: '200px', overflow: 'hidden' }}
                                            // @ts-expect-error(bla)
                                            onChange={handleSelectAssignedChange}
                                            inputProps={{
                                                id: 'select-assigned',
                                                style: {
                                                    height: '200px',
                                                    paddingTop: '25px',
                                                    paddingLeft: '10px',
                                                    paddingRight: '10px',
                                                    overflow: 'scroll',
                                                },
                                            }}
                                        >
                                            {[...listMemberAssigned]
                                                .sort((a, b) =>
                                                    (a.member.lastname || '').localeCompare(
                                                        b.member.lastname || '',
                                                    ),
                                                )
                                                .map((listMember) => (
                                                    <option key={listMember.uuid} value={listMember.uuid}>
                                                        {listMember.member.lastname}{' '}
                                                        {listMember.member.firstname}
                                                    </option>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion >
                )
                }
                <Box>
                    <ListMemberList listHeader={listHeader} listMemberAssigned={listMemberAssigned} listMemberAvailable={listMemberAvailable} />
                </Box>
            </Box>
        </>
    );
}
